import { useTranslation } from "react-i18next";
import {
  VerificationBadge,
  type VerificationStatus,
} from "~/components/common/VerificationBadge";

type Props = {
  name: string | null;
  commonName: string | null;
  description: string | null;
  logoUrl: string | null;
  statusCode: string | null;
  approvalStatus: string | null;
};

const verificationLabelKey: Record<VerificationStatus, string> = {
  verified: "Verification.verified",
  pending: "Verification.pending",
  unverified: "Verification.unverified",
};

const deriveStatus = (
  statusCode: string | null,
  approvalStatus: string | null,
): VerificationStatus => {
  if (approvalStatus === "APPROVED" && statusCode === "ACTIVE") {
    return "verified";
  }
  if (approvalStatus === "PENDING") return "pending";
  return "unverified";
};

export const MerchantHeader = ({
  name,
  commonName,
  description,
  logoUrl,
  statusCode,
  approvalStatus,
}: Props) => {
  const { t } = useTranslation();
  const status = deriveStatus(statusCode, approvalStatus);

  return (
    <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
      {logoUrl?.startsWith("http") ? (
        <img
          src={logoUrl}
          alt={name ?? ""}
          className="h-20 w-20 shrink-0 rounded-2xl bg-surface-elevated object-cover p-1"
        />
      ) : (
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-raytio-viola/15 font-heading text-3xl font-bold text-raytio-viola-text">
          {Array.from(name ?? "?")[0]}
        </div>
      )}
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-heading text-3xl font-bold text-text-full">
            {name ?? t("MerchantDetail.unnamed")}
          </h1>
          <VerificationBadge
            status={status}
            label={t(verificationLabelKey[status])}
          />
        </div>
        {commonName && (
          <p className="mt-1 text-lg text-text-light">{commonName}</p>
        )}
        {description && <p className="mt-4 text-text-medium">{description}</p>}
      </div>
    </div>
  );
};
